import React, { useState } from 'react';
import axios from 'axios';
import { ShieldCheck, Eye, EyeSlash } from '@phosphor-icons/react';
import { useAuth, formatApiErrorDetail } from '../auth/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Blocking modal shown to a customer the first time they log in. They must tick
 * the acknowledgement checkbox before they can continue into the app.
 *
 * Renders nothing for non-customers or for customers who already acknowledged
 * (server-side flag `confidentialityAcceptedAt`). Once acknowledged, refreshes
 * `me` so the modal unmounts cleanly.
 *
 * Includes an inline "change password now" flow that posts to /api/auth/change-password
 * — there's no standalone password-change page in the app, so the modal hosts the
 * full UX. Acknowledgement is captured BEFORE the password form is shown so the
 * customer can't bypass it by closing the browser mid-flow.
 */
const NOTICE = (
  'Please keep your login details confidential. Your username and password are for your '
  + 'use only and must not be shared. Please change your temporary password immediately. '
  + 'Orders placed using your login details are your responsibility. If you believe your '
  + 'login details have been shared or compromised, please change your password as soon as possible.'
);

const CustomerConfidentialityModal = () => {
  const { user, refresh } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState('notice'); // 'notice' | 'password' | 'done'
  const [err, setErr] = useState(null);

  // Password change state (lazily used)
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwOk, setPwOk] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  // Only render for customers who haven't yet acknowledged. We treat user===null
  // (auth still loading) and user===false (signed out) as "render nothing".
  if (!user || user.role !== 'customer' || user.confidentialityAcceptedAt) {
    return null;
  }

  const acknowledge = async (afterAck) => {
    if (!accepted || submitting) return;
    setSubmitting(true);
    setErr(null);
    try {
      await axios.post(`${API}/auth/acknowledge-confidentiality`);
      if (afterAck === 'password') {
        setStage('password');
      } else {
        // Refresh `me` so the modal unmounts via the guard above.
        await refresh();
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const submitNewPassword = async (e) => {
    e?.preventDefault?.();
    setErr(null);
    if (newPw.length < 8) {
      setErr('New password must be at least 8 characters.');
      return;
    }
    if (newPw !== confirmPw) {
      setErr('New password and confirmation do not match.');
      return;
    }
    setPwBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPw,
        new_password: newPw,
      });
      setPwOk(true);
      // Brief success state, then dismiss the modal.
      setTimeout(async () => { await refresh(); }, 900);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      data-testid="customer-confidentiality-modal"
    >
      <div className="bg-surface border border-border-color rounded-xl w-full max-w-lg overflow-hidden shadow-2xl">
        <div className="flex items-start gap-3 p-6 border-b border-border-color">
          <ShieldCheck size={28} weight="duotone" className="text-gold shrink-0" />
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold font-heading text-text-primary" data-testid="confidentiality-modal-title">
              {stage === 'password' ? 'Change your password' : 'Important — login confidentiality'}
            </h2>
            <p className="text-xs text-text-secondary mt-1">
              {stage === 'password'
                ? 'Set a new password. Minimum 8 characters.'
                : 'Please read and acknowledge before continuing.'}
            </p>
          </div>
        </div>

        {stage === 'notice' && (
          <div className="p-6">
            <p
              className="text-sm text-text-primary leading-relaxed whitespace-pre-line"
              data-testid="confidentiality-modal-notice"
            >
              {NOTICE}
            </p>
            <label
              className="mt-5 flex items-start gap-3 cursor-pointer select-none"
              data-testid="confidentiality-acknowledge-label"
            >
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                disabled={submitting}
                data-testid="confidentiality-acknowledge-checkbox"
                className="mt-1 accent-gold w-4 h-4 shrink-0"
              />
              <span className="text-sm text-text-primary">
                I have read and understood the above.
              </span>
            </label>
            {err && (
              <p
                className="mt-4 text-xs text-danger bg-danger/10 border border-danger/30 rounded px-3 py-2"
                data-testid="confidentiality-modal-error"
              >
                {err}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-2 mt-6">
              <button
                type="button"
                onClick={() => acknowledge('password')}
                disabled={!accepted || submitting}
                data-testid="confidentiality-change-password-button"
                className="flex-1 px-4 py-2 text-sm font-semibold rounded-lg border border-gold text-gold hover:bg-gold/10 disabled:opacity-40 disabled:cursor-not-allowed"
                title={!accepted ? 'Tick the acknowledgement checkbox first.' : 'Acknowledge and change your password now'}
              >
                Change my password now
              </button>
              <button
                type="button"
                onClick={() => acknowledge('continue')}
                disabled={!accepted || submitting}
                data-testid="confidentiality-acknowledge-button"
                className="flex-1 px-4 py-2 text-sm font-bold rounded-lg bg-gold text-black hover:bg-gold-hover disabled:opacity-40 disabled:cursor-not-allowed"
                title={!accepted ? 'Tick the acknowledgement checkbox first.' : 'Acknowledge and continue to the app'}
              >
                {submitting ? 'Saving…' : 'I acknowledge'}
              </button>
            </div>
          </div>
        )}

        {stage === 'password' && (
          <form className="p-6 space-y-4" onSubmit={submitNewPassword} data-testid="confidentiality-password-form">
            <div>
              <label className="text-xs text-text-secondary mb-1 block">Current (temporary) password</label>
              <div className="relative">
                <input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  required
                  autoComplete="current-password"
                  data-testid="confidentiality-current-password"
                  className="w-full px-3 py-2 pr-10 text-sm bg-background border border-border-color rounded-lg text-text-primary focus:outline-none focus:border-gold"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary hover:text-gold"
                  tabIndex={-1}
                  aria-label={showCurrent ? 'Hide current password' : 'Show current password'}
                >
                  {showCurrent ? <EyeSlash size={16} weight="duotone" /> : <Eye size={16} weight="duotone" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs text-text-secondary mb-1 block">New password (min 8 characters)</label>
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  data-testid="confidentiality-new-password"
                  className="w-full px-3 py-2 pr-10 text-sm bg-background border border-border-color rounded-lg text-text-primary focus:outline-none focus:border-gold"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary hover:text-gold"
                  tabIndex={-1}
                  aria-label={showNew ? 'Hide new password' : 'Show new password'}
                >
                  {showNew ? <EyeSlash size={16} weight="duotone" /> : <Eye size={16} weight="duotone" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs text-text-secondary mb-1 block">Confirm new password</label>
              <input
                type={showNew ? 'text' : 'password'}
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                data-testid="confidentiality-confirm-password"
                className="w-full px-3 py-2 text-sm bg-background border border-border-color rounded-lg text-text-primary focus:outline-none focus:border-gold"
              />
            </div>
            {err && (
              <p
                className="text-xs text-danger bg-danger/10 border border-danger/30 rounded px-3 py-2"
                data-testid="confidentiality-password-error"
              >
                {err}
              </p>
            )}
            {pwOk && (
              <p
                className="text-xs text-success bg-success/10 border border-success/30 rounded px-3 py-2"
                data-testid="confidentiality-password-success"
              >
                Password updated. Loading your account…
              </p>
            )}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={async () => { await refresh(); }}
                disabled={pwBusy || pwOk}
                data-testid="confidentiality-skip-password-button"
                className="flex-1 px-4 py-2 text-sm border border-border-color text-text-secondary rounded-lg hover:border-gold hover:text-gold disabled:opacity-40"
              >
                Skip for now
              </button>
              <button
                type="submit"
                disabled={pwBusy || pwOk || !currentPw || !newPw || !confirmPw}
                data-testid="confidentiality-submit-password-button"
                className="flex-1 px-4 py-2 text-sm font-bold bg-gold text-black rounded-lg hover:bg-gold-hover disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {pwBusy ? 'Updating…' : 'Update password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CustomerConfidentialityModal;
